import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Modal from "../common/Modal";
import { useIndexDb } from "../../hooks/useIndexDb";
import { useBulkSaveRecordings } from "../../hooks/useBulkSaveRecordings";
import { formatRecordingDuration, queuedAudioSecs } from "../../utils/recordingList";
import { canSaveAudioToDevice } from "../../utils/saveAudioFile";

// Shown once per session, after the sign-in drain pass, when this device is
// holding more than BACKLOG_ALERT_SECS of audio that has reached no encounter.
//
// The queue already had a summary toast: one per failing session, click to open
// Local Recordings, gone in six seconds. It was not enough. A clinician carried
// ~13 hours of un-synced audio for weeks (Sentry, 7 Sep 2026: "Found 4728 unsynced
// recordings", on a browser that had also refused persistent storage) and the way
// it finally came to light was her asking support why nothing worked. A toast that
// closes itself is a notice; this is a question that has to be answered.
//
// It leads with DURATION rather than a row count because that is the thing a
// clinician can weigh — "3h 15m of audio" is an amount of work and an amount of
// risk; "4728 rows" is a number about a database.
function RecordingsBacklogAlert() {
  const { t } = useTranslation();
  const { showBacklogAlert, setShowBacklogAlert, setShowRecordingsModal, recordings, deleteRecordings } = useIndexDb();
  const { bulkSave, saveAll, saveAllLabel } = useBulkSaveRecordings();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const list = recordings || [];
  const canSaveAudio = canSaveAudioToDevice();
  const busy = !!bulkSave || deleting;
  // Never in the native shell. It has its own Local Recordings screen over its own
  // store, and both of this alert's answers are hollow there: Save is hidden (an
  // anchor download does nothing in the WebView) and Retry is a no-op for the rows
  // it would list. Layout.js already does not mount it there; this is the same
  // rule held where it can be tested, so it survives a change to that line. The
  // same signal canSaveAudioToDevice and the modal read.
  const isNativeShell = typeof window !== "undefined" && window.ReactNativeWebView !== undefined;

  // The alert fires off a drain pass and the queue can drain between that decision
  // and this render. An alert about nothing is how people learn to dismiss alerts —
  // so it is not rendered, AND the flag is put down. Leaving it armed over an empty
  // list meant the next non-empty list (the recording that just finished and is
  // waiting its turn to upload) popped the modal with no threshold at all.
  useEffect(() => {
    if (showBacklogAlert && list.length === 0) setShowBacklogAlert(false);
  }, [showBacklogAlert, list.length, setShowBacklogAlert]);

  if (isNativeShell || !showBacklogAlert || list.length === 0) return null;

  const duration = formatRecordingDuration(queuedAudioSecs(list));

  const close = () => {
    setConfirmDelete(false);
    setShowBacklogAlert(false);
  };

  const review = () => {
    setShowRecordingsModal(true);
    close();
  };

  const deleteAll = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      // One transaction for the whole queue (deleteRecordings never throws; the
      // catch below is for the unexpected). "Deleted N" is only said when every
      // row actually went — a partial delete reported as done is a queue that
      // still holds audio somebody now believes is gone.
      const result = await deleteRecordings(list.map((r) => r.keys));
      if (result?.ok) {
        toast.success(t("layout.recordingsBacklog.deleteAllDone", { count: list.length }));
        close();
      } else {
        toast.error(t("layout.recordingsBacklog.deleteAllFailed"));
      }
    } catch (e) {
      console.error("Could not delete the queued recordings", e?.name || e);
      toast.error(t("layout.recordingsBacklog.deleteAllFailed"));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal
      show={true}
      // Modal disables its X when there is no close handler: a save or delete in
      // flight must not be abandoned by a reflex click on the corner.
      close={busy ? undefined : close}
      title={t("layout.recordingsBacklog.title")}
      size="small"
      className="recordings-backlog-modal"
      action={
        <>
          <button className="white" onClick={close} disabled={busy}>
            {t("layout.recordingsBacklog.later")}
          </button>
          <button onClick={review} disabled={busy}>
            {t("layout.recordingsBacklog.review")}
          </button>
        </>
      }
    >
      <div className="recordings-backlog">
        <div className="backlog-headline">
          <i className="notranslate material-icons-outlined">graphic_eq</i>
          <span>{t("layout.recordingsBacklog.body", { duration, count: list.length })}</span>
        </div>
        {/* Every segmented length is segment-count x nominal length, and an
            unfinished recording never declared its total, so the figure above is a
            lower bound. Saying it flat would claim a precision the data lacks. */}
        <span className="backlog-approx">{t("layout.recordingsBacklog.approx")}</span>
        <span className="backlog-explain">{t("layout.recordingsBacklog.explain")}</span>

        {!confirmDelete && (
          <div className="backlog-actions">
            <button className="rec-btn danger" onClick={() => setConfirmDelete(true)} disabled={busy}>
              <i className="notranslate material-icons-outlined">delete_sweep</i>
              <span>{t("layout.recordingsBacklog.deleteAll", { count: list.length })}</span>
            </button>
          </div>
        )}

        {confirmDelete && (
          <div className="recording-confirm">
            {/* This is unsynchronised clinical audio and for some of it this device
                holds the only copy. One click, on a dialog that appeared unbidden
                while somebody was trying to start their day, must not destroy it. */}
            <span className="confirm-text">
              {t("layout.recordingsBacklog.deleteAllConfirm", { count: list.length, duration })}
            </span>
            <div className="confirm-actions">
              <button className="rec-btn" onClick={() => setConfirmDelete(false)} disabled={busy}>
                <span>{t("common.cancel")}</span>
              </button>
              {canSaveAudio && (
                <button className="rec-btn" onClick={() => saveAll(list)} disabled={busy}>
                  <span>{bulkSave ? saveAllLabel(list.length) : t("layout.recordingsBacklog.saveAll")}</span>
                </button>
              )}
              <button className="rec-btn danger-solid" onClick={deleteAll} disabled={busy}>
                <span>{t("layout.recordingsBacklog.confirmDeleteAll", { count: list.length })}</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default RecordingsBacklogAlert;
